// TrustBadges.jsx
import React, { useEffect } from "react";

export default function TrustBadges() {
  useEffect(() => {
    const els = document.querySelectorAll(".trust-reveal");

    const onScroll = () => {
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add("active");
        }
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const badges = [
    {
      title: "Fast Turnaround",
      desc: "Most projects delivered in days, not months — with weekly previews.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path
            fill="currentColor"
            d="M12 2L4 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-3zm0 2.18l6 2.24v5.23c0 4.21-2.84 8.51-6 9.77-3.16-1.26-6-5.56-6-9.77V6.42l6-2.24z"
          />
        </svg>
      ),
    },
    {
      title: "Secure Data Handling",
      desc: "Encrypted, private, and built using industry-standard best practices.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path
            fill="currentColor"
            d="M12 2a5 5 0 00-5 5v3H5v12h14V10h-2V7a5 5 0 00-5-5zm-3 5a3 3 0 016 0v3H9V7zm3 12a2 2 0 110-4 2 2 0 010 4z"
          />
        </svg>
      ),
    },
    {
      title: "100% Custom Builds",
      desc: "No templates. No drag-and-drop. Fully tailored to your business.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path
            fill="currentColor"
            d="M21 3H3v18h18V3zm-2 16H5V5h14v14zM8 7h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z"
          />
        </svg>
      ),
    },
    {
      title: "Lifetime Support",
      desc: "Ongoing updates, fixes, hosting, analytics, and future feature upgrades.",
      svg: (
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#785f14]">
          <path
            fill="currentColor"
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 
            4 4 6.5 4c1.74 0 3.41 1.01 4.13 2.44h.74C14.09 5 15.76 
            4 17.5 4 20 4 22 6 22 8.5c0 3.78-3.4 6.86-8.55 
            11.54L12 21.35z"
          />
        </svg>
      ),
    },
  ];

  return (
    <section className="py-16 px-4 relative">
      <style>{`
        /* Fade-in animation */
        .trust-reveal {
          opacity: 0;
          transform: translateY(35px) scale(0.96);
          transition: opacity 0.65s ease-out, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .trust-reveal.active {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        .trust-card {
          background: rgba(255,255,255,0.72);
          backdrop-filter: blur(14px);
          border: 1px solid rgba(120,95,20,0.18);
          border-radius: 20px;
          padding: 20px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .trust-card:hover {
          transform: translateY(-6px) scale(1.03);
          box-shadow: 0 12px 28px rgba(0,0,0,0.14);
        }

        .icon-glow {
          filter: drop-shadow(0 0 6px rgba(120,95,20,0.4));
        }

        /* Float animation (soft, premium) */
        .float-up {
          animation: floatUp 6s ease-in-out infinite;
        }

        @keyframes floatUp {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        /* Mobile compact layout */
        @media (max-width: 640px) {
          .trust-card {
            padding: 14px !important;
            border-radius: 16px !important;
          }
          .trust-card h3 {
            font-size: 0.9rem !important;
          }
          .trust-card p {
            font-size: 0.75rem !important;
            line-height: 1.25rem;
          }
        }
      `}</style>

      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14] trust-reveal">
          Why Businesses Trust <span className="font-semibold">SavvyTron</span>
        </h2>

        <p className="text-gray-600 mt-2 max-w-xl mx-auto trust-reveal">
          Premium quality, engineered precision, and long-term reliability — every time.
        </p>

        {/* Grid */}
        <div
          className="
            mt-12
            grid
            grid-cols-2
            sm:grid-cols-2
            lg:grid-cols-4
            gap-4 sm:gap-6
          "
        >
          {badges.map((item, i) => (
            <div
              key={i}
              className="trust-reveal trust-card shadow-sm float-up"
            >
              <div className="flex justify-center mb-3 icon-glow">
                {item.svg}
              </div>

              <h3 className="text-[#785f14] font-semibold text-base mb-1">
                {item.title}
              </h3>

              <p className="text-gray-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
